import { Target, Eye, Compass } from "lucide-react";
import { Reveal, Marquee } from "../components/Reveal";
import { PageHero, SectionHeading, CTABand, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang, useContent } from "../i18n";

const COPY = {
  fr: {
    meta: "À propos",
    eyebrow: "À propos de DOC TOGO",
    h1a: "Nés au Togo,",
    h1b: "au service de tous.",
    sub: "DOC TOGO est née d'un constat simple : les démarches administratives au Togo sont trop souvent un parcours du combattant — surtout à distance. Nous avons décidé de changer cela.",
    mission: "Notre mission",
    missionText: "Rendre chaque démarche au Togo simple, rapide et accessible — que l'on vive à Lomé, à Paris ou à Toronto. Un besoin, une démarche, un accompagnement.",
    vision: "Notre vision",
    visionText: "Devenir la plateforme de référence de l'accompagnement administratif et entrepreneurial au Togo, reconnue pour sa fiabilité par la diaspora comme par les investisseurs internationaux.",
    approche: "Notre approche",
    approcheText: "La rigueur d'un cabinet, la fluidité d'une plateforme digitale : formulaires en ligne, transmission sécurisée des documents, suivi en temps réel et conseiller dédié.",
    figures: [
      { value: "+1 500", label: "démarches accompagnées" },
      { value: "20+", label: "pays desservis" },
      { value: "24h", label: "délai de réponse" },
      { value: "99,4%", label: "de dossiers aboutis" },
    ],
    valuesEyebrow: "Nos valeurs",
    valuesTitle: "Six engagements, zéro compromis.",
    valuesSub: "Chaque dossier confié à DOC TOGO est traité selon les mêmes principes, sans exception.",
    ctaTitle: "Faites confiance à une équipe qui connaît le terrain.",
    ctaSub: "Parlez-nous de votre démarche : réponse personnalisée sous 24h ouvrées.",
    imgAlt: "Lomé, Togo",
  },
  en: {
    meta: "About us",
    eyebrow: "About DOC TOGO",
    h1a: "Born in Togo,",
    h1b: "serving everyone.",
    sub: "DOC TOGO was born from a simple observation: administrative procedures in Togo are too often an obstacle course — especially from abroad. We decided to change that.",
    mission: "Our mission",
    missionText: "Make every procedure in Togo simple, fast and accessible — whether you live in Lomé, Paris or Toronto. One need. One process. One partner.",
    vision: "Our vision",
    visionText: "Become the reference platform for administrative and business support in Togo, recognised for its reliability by the diaspora and international investors alike.",
    approche: "Our approach",
    approcheText: "The rigour of a firm, the fluidity of a digital platform: online forms, secure document transfer, real-time tracking and a dedicated advisor.",
    figures: [
      { value: "1,500+", label: "procedures supported" },
      { value: "20+", label: "countries served" },
      { value: "24h", label: "response time" },
      { value: "99.4%", label: "of cases completed" },
    ],
    valuesEyebrow: "Our values",
    valuesTitle: "Six commitments, zero compromise.",
    valuesSub: "Every case entrusted to DOC TOGO is handled according to the same principles, without exception.",
    ctaTitle: "Trust a team that knows the field.",
    ctaSub: "Tell us about your procedure: personalised reply within 24 business hours.",
    imgAlt: "Lomé, Togo",
  },
};

export default function About() {
  const { lang } = useLang();
  const { VALUES, MARQUEE_ITEMS } = useContent();
  const c = COPY[lang];
  usePageTitle(c.meta);
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.hero}
        imageAlt={c.imgAlt}
      />
      <Marquee items={MARQUEE_ITEMS} dark />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Reveal>
              <div data-testid="about-mission" className="h-full rounded-3xl bg-brand p-9 text-white">
                <Target className="h-8 w-8 text-gold" />
                <h3 className="mt-5 font-display text-2xl font-bold">{c.mission}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70 lg:text-base">{c.missionText}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="about-vision" className="h-full rounded-3xl border border-brand/10 bg-white p-9 shadow-sm">
                <Eye className="h-8 w-8 text-gold" />
                <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">{c.vision}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 lg:text-base">{c.visionText}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div data-testid="about-approach" className="h-full rounded-3xl border border-brand/10 bg-white p-9 shadow-sm">
                <Compass className="h-8 w-8 text-gold" />
                <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">{c.approche}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 lg:text-base">{c.approcheText}</p>
              </div>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-6 rounded-3xl border border-brand/10 bg-white p-10 shadow-sm lg:grid-cols-4">
            {c.figures.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.08}>
                <div data-testid={`about-figure-${i}`} className="text-center">
                  <div className="font-display text-3xl font-extrabold text-brand lg:text-4xl">{f.value}</div>
                  <div className="mt-1.5 text-xs text-slate-500 lg:text-sm">{f.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading align="center" eyebrow={c.valuesEyebrow} title={c.valuesTitle} sub={c.valuesSub} />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div
                  data-testid={`about-value-${i}`}
                  className="group h-full rounded-3xl border border-brand/10 bg-ivory p-8 transition-[transform,box-shadow,background-color,border-color] duration-300 hover:-translate-y-1 hover:border-brand hover:bg-brand hover:shadow-xl"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-light text-brand transition-colors duration-300 group-hover:bg-white/10 group-hover:text-gold">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 transition-colors duration-300 group-hover:text-white/70">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTABand title={c.ctaTitle} sub={c.ctaSub} />
    </div>
  );
}
